import React from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import type { FormDescriptionImage } from "@f2e/shared/types"
import { FieldDescription } from "@f2e/ui/components/InteractionRequestForm"

import { BaseFieldEditor, BaseFieldProps } from "./BaseFieldEditor"
import { Input, Label } from "@f2e/ui/components/base"

export type NumberField = BaseFieldProps<{
  type: "number"
  name: string
  question: string
  label: string
  placeholder: string
  required: boolean
  min?: number
  max?: number
  step?: number
}>

export const NumberFieldEditor: React.FC<NumberField> = ({
  field,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation()
  return (
    <>
      <BaseFieldEditor field={field} onChange={onChange} disabled={disabled} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">
            {t("workspace.agent.basicForm.formBuilder.numberField.min")}
          </Label>
          <Input
            type="number"
            value={field.min ?? ""}
            onChange={(e) =>
              onChange({
                ...field,
                min: e.target.value === "" ? undefined : Number(e.target.value),
              } as NumberField["field"])
            }
            disabled={disabled}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">
            {t("workspace.agent.basicForm.formBuilder.numberField.max")}
          </Label>
          <Input
            type="number"
            value={field.max ?? ""}
            onChange={(e) =>
              onChange({
                ...field,
                max: e.target.value === "" ? undefined : Number(e.target.value),
              } as NumberField["field"])
            }
            disabled={disabled}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">
            {t("workspace.agent.basicForm.formBuilder.numberField.step")}
          </Label>
          <Input
            type="number"
            value={field.step ?? ""}
            onChange={(e) =>
              onChange({
                ...field,
                step:
                  e.target.value === "" ? undefined : Number(e.target.value),
              } as NumberField["field"])
            }
            disabled={disabled}
          />
        </div>
      </div>
    </>
  )
}

export default NumberFieldEditor

// Validation for runtime usage
export function validateNumberField(
  field: { name: string; required?: boolean; min?: number; max?: number },
  value: unknown,
  t: TFunction
): string | undefined {
  const v = value as unknown
  const str = v === undefined || v === null ? "" : String(v)
  if (field.required && str.trim() === "") {
    return t("workspace.agent.basicForm.formBuilder.validation.required")
  }
  if (str.trim() === "") return undefined
  const num = Number(str)
  if (Number.isNaN(num)) {
    return t("workspace.agent.basicForm.formBuilder.validation.numberInvalid")
  }
  if (typeof field.min === "number" && num < field.min) {
    return t("workspace.agent.basicForm.formBuilder.validation.numberMin", {
      min: field.min,
    })
  }
  if (typeof field.max === "number" && num > field.max) {
    return t("workspace.agent.basicForm.formBuilder.validation.numberMax", {
      max: field.max,
    })
  }
  return undefined
}

export const NumberFieldRenderer: React.FC<{
  field: {
    question: string
    placeholder: string
    required: boolean
    min?: number
    max?: number
    step?: number
    helpText?: string
    descriptionImage?: FormDescriptionImage
  }
  mode: "preview" | "runtime"
  value?: string | number
  onChange?: (v: string) => void
  disabled?: boolean
  error?: string
}> = ({ field, mode, value, onChange, disabled, error }) => {
  const isPreview = mode === "preview"
  const [local, setLocal] = React.useState<string>("")
  const val = isPreview ? local : value !== undefined ? String(value) : ""
  const setVal = (v: string) => {
    if (isPreview) setLocal(v)
    else onChange?.(v)
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs">
        {field.question}{" "}
        {field.required ? <span className="text-red-500">*</span> : null}
      </Label>
      <FieldDescription
        helpText={field.helpText}
        descriptionImage={field.descriptionImage}
      />
      <Input
        type="number"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled}
        min={field.min}
        max={field.max}
        step={field.step ?? "any"}
        className={
          error ? "border-red-500 focus-visible:ring-red-500" : undefined
        }
      />
      {error ? <div className="text-xs text-red-500">{error}</div> : null}
    </div>
  )
}
